import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { ReportDecision } from './api-decisions';
import { RefuseDialog } from './RefuseDialog';
import { NoteField } from './NoteField';
import type { Report } from './types';

/**
 * The foot of the decision page (spec §1.4): the note, and «Approva», «Rimanda con una nota» and
 * «Rifiuta la nota» (the last asks for confirmation). On a phone the buttons stack, «Approva»
 * first. The note is mandatory for the last two.
 */
export function DecisionPanel({
  report,
  busy,
  onDecide,
}: {
  readonly report: Report;
  readonly busy: boolean;
  readonly onDecide: (kind: ReportDecision, note: string) => void;
}) {
  const { t } = useTranslation('expenses');
  const [note, setNote] = useState('');
  const [missing, setMissing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const can = report.can ?? {};
  // #864: «Approva» stays visible but off while something stops it, with the reason under it.
  const approveReasons = can.approve === true ? [] : (report.approveBlockers ?? []);
  const showApprove = can.approve === true || (can.sendBack === true && approveReasons.length > 0);

  const decide = (kind: ReportDecision) => {
    if (kind !== 'approve' && note.trim() === '') {
      setMissing(true);
      return;
    }
    if (kind === 'refuse' && !confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    onDecide(kind, note);
    setNote('');
  };

  return (
    <div
      className="border-border bg-card flex flex-col gap-3 rounded-xl border p-3"
      data-testid="decision-panel"
    >
      <NoteField
        label={t('reports.page.decision.note')}
        hint={t('reports.page.decision.noteRequired')}
        value={note}
        invalidMessage={missing ? t('reports.page.decision.noteMissing') : undefined}
        onChange={(value) => {
          setNote(value);
          if (value.trim() !== '') setMissing(false);
        }}
      />
      <div className="flex flex-col gap-2 @min-[620px]/shell:flex-row @min-[620px]/shell:flex-wrap">
        {showApprove ? (
          <Button
            type="button"
            disabled={can.approve !== true || busy}
            aria-describedby={approveReasons.length > 0 ? 'approve-blockers' : undefined}
            onClick={() => decide('approve')}
          >
            {t('reports.page.decision.approve')}
          </Button>
        ) : null}
        {can.sendBack === true ? (
          <Button type="button" variant="outline" disabled={busy} onClick={() => decide('return')}>
            {t('reports.page.decision.sendBack')}
          </Button>
        ) : null}
        {can.refuse === true ? (
          <Button
            type="button"
            variant="outline-destructive"
            disabled={busy}
            onClick={() => decide('refuse')}
          >
            {t('reports.page.decision.refuse')}
          </Button>
        ) : null}
      </div>
      {approveReasons.length > 0 ? (
        <div id="approve-blockers" className="flex flex-col gap-1">
          {[...new Set(approveReasons.map((blocker) => blocker.code))].map((code) => (
            <span key={code} className="text-muted-foreground text-sm">
              {t(`reports.page.decision.approveBlockers.${code}`)}
            </span>
          ))}
        </div>
      ) : null}
      {confirming ? (
        <RefuseDialog
          number={report.number}
          busy={busy}
          onConfirm={() => decide('refuse')}
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
}
